/**
 * Siting emplacements (03 §5.1 step 6): north of the thing they cover - a road crossing, a
 * gap in the wire, a gate - a little to one side, facing south down a clear field of fire,
 * never into a wall, never over the drop band and never over the pickup.
 */

import { angleDiff, atan2, PI } from '../sim/math';
import type { Pcg32 } from '../sim/rng';
import { ENEMY } from '../sim/tuning';
import type { EmplacementKind } from './biomes';
import { NO_BUILD } from './builder';
import { FLAG } from './grid';
import { overlooksQuietApproach } from './quietSide';
import type { CoverPoint, GenCtx, Point } from './types';

/** Where the gunner sits relative to the prop (yaw 0: the nest's bags and the bunker's slit face south). */
const GUNNER_DZ: Record<EmplacementKind, number> = { mgNest: 0.2, bunker: 1.2, mannedTurret: 0, turretBunker: 0 };

const ARC = (ENEMY.emplacementArcDeg + 5) * (PI / 180);
const STAND_OFF = ENEMY.emplacementRange + 2;

export function siteEmplacement(ctx: GenCtx, rng: Pcg32, kind: EmplacementKind, cover: Point, elite: boolean): boolean {
  const drop = ctx.bands[0];
  const { pickup } = ctx.plan;
  for (let tries = 0; tries < 28; tries++) {
    const x = Math.floor(cover.x + rng.range(3, 9) * (rng.chance(0.5) ? 1 : -1)) + 0.5;
    const z = Math.floor(cover.z - rng.range(8, 15)) + 0.5;
    if (z < 4 || (drop && z > drop.z0 - STAND_OFF)) continue;
    if (!ctx.map.fits(kind, x, z, { margin: 1.4, avoid: NO_BUILD | FLAG.path })) continue;
    if (!ctx.map.mask.clearLine(x, z, cover.x, cover.z, FLAG.solid)) continue;
    // The gunner watches the thing he covers, as far round as a south-facing position lets him.
    const aim = Math.max(-0.9, Math.min(0.9, atan2(cover.x - x, cover.z - z)));
    const toPickup = Math.abs(angleDiff(aim, atan2(pickup.x - x, pickup.z - z)));
    const pickupDist = Math.sqrt((pickup.x - x) * (pickup.x - x) + (pickup.z - z) * (pickup.z - z));
    if (pickupDist < STAND_OFF && toPickup < ARC) continue;
    const gunner = { kind: elite ? 'elite' : 'rifleman', x, z: z + GUNNER_DZ[kind], facing: aim } as const;
    // The guns cover the road and the crossings; the open end of the quiet way in is the patrols' to watch.
    if (overlooksQuietApproach(ctx, gunner, kind)) continue;

    const prop = ctx.map.add(kind, x, z);
    ctx.map.spawn({ ...gunner, mans: prop.id });
    // Nothing placed later may grow up in his field of fire.
    const t = 4 / Math.max(4, Math.sqrt((cover.x - x) * (cover.x - x) + (cover.z - z) * (cover.z - z)));
    ctx.map.mask.line([[x + (cover.x - x) * t, z + (cover.z - z) * t], [cover.x, cover.z]], 0.6, FLAG.keep);
    return true;
  }
  return false;
}

/** Points worth covering: every crossing of every line, and the compound's gates. */
export function collectCoverPoints(ctx: GenCtx): CoverPoint[] {
  const out: CoverPoint[] = [];
  ctx.plan.lines.forEach((line, i) => {
    // The band north of line i is band i + 1; the first line borders the drop band and is left alone.
    if (i === 0) return;
    for (const g of line.gaps) out.push({ x: g.x, z: line.z, band: i + 1, used: false });
  });
  const goal = ctx.bands.length - 1;
  for (const g of ctx.plan.gates) {
    const inward = g.side === 'south' ? 0 : g.side === 'west' ? 6 : -6;
    out.push({ x: g.x + inward, z: g.side === 'south' ? g.z : g.z + 2, band: goal, used: false });
  }
  for (const b of ctx.map.waterBarriers) {
    for (const x of b.crossings) out.push({ x, z: (ctx.bands[goal - 1]?.z0 ?? 0) + 8, band: goal - 1, used: false });
  }
  return out;
}

export function weightedKind(
  rng: Pcg32, weights: Record<EmplacementKind, number>, allowed: readonly EmplacementKind[],
): EmplacementKind {
  let total = 0;
  for (const k of allowed) total += weights[k];
  let roll = rng.float() * total;
  for (const k of allowed) {
    roll -= weights[k];
    if (roll < 0) return k;
  }
  return allowed[0] ?? 'mgNest';
}
